import { useMemoMergePropsWithDefault } from '@elliemae/ds-props-helpers';
import React from 'react';
import { useSelectFirstBodyItem } from '../hooks/useSelectFirstBodyItem.js';
import type { DSLeftNavigationT } from '../react-desc-prop-types.js';
import { defaultProps } from '../react-desc-prop-types.js';
import useLeftNavItems from './useLeftNavItems.js';
import { useLeftNavSmoothExpand } from './useLeftNavSmoothExpand.js';

export const useLeftNavConfig = (props: DSLeftNavigationT.Props) => {
  const propsWithDefaults = useMemoMergePropsWithDefault<DSLeftNavigationT.InternalProps>(props, defaultProps);
  const { expanded, openedItem, onSelectedChange, onFocusChange, items } = propsWithDefaults;
  // -----------------------------------------------------------------------------
  // ************************** ------------ **************************
  // *                         INTERNAL STATE                         *
  // ************************** ------------ **************************
  // Selection related
  const [selectedItem, setSelectedItem] = React.useState<string | null>(null);
  const [selectedParent, setSelectedParent] = React.useState<string | null>(null);
  // Focus related
  const [focusedItem, setFocusedItem] = React.useState<string | null>(null);
  // Opened uncontrolled drilldowns
  const [openedDrilldowns, setOpenedDrilldowns] = React.useState<string[]>([]);

  // -----------------------------------------------------------------------------
  // ************************** ------------ **************************
  // *                        EXPAND ANIMATION                        *
  // ************************** ------------ **************************
  const [expandedForAnimation, setExpandedForAnimation] = React.useState(expanded);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const smoothExpandOpts = React.useMemo(
    () => ({
      expandedForAnimation,
      timeoutRef,
      setExpandedForAnimation,
    }),
    [expandedForAnimation, timeoutRef, setExpandedForAnimation],
  );
  useLeftNavSmoothExpand(propsWithDefaults, smoothExpandOpts);

  // -----------------------------------------------------------------------------
  // ************************** ------------ **************************
  // *                          SIDE EFFECTS                          *
  // ************************** ------------ **************************
  React.useEffect(() => {
    if (onSelectedChange) onSelectedChange(selectedItem);
  }, [selectedItem, onSelectedChange]);

  React.useEffect(() => {
    if (propsWithDefaults.actionRef && propsWithDefaults.actionRef.current) {
      propsWithDefaults.actionRef.current.setFocusedItem = setFocusedItem;
    }
  }, [propsWithDefaults.actionRef]);

  React.useEffect(() => {
    if (openedItem) setSelectedParent(openedItem);
  }, [openedItem]);

  React.useEffect(() => {
    if (onFocusChange) onFocusChange(focusedItem);
  }, [focusedItem, onFocusChange]);

  useSelectFirstBodyItem(
    items ?? [],
    openedItem ?? '',
    propsWithDefaults.selectedItem !== undefined ? propsWithDefaults.selectedItem : selectedItem,
    setSelectedItem,
    propsWithDefaults.disableDefaultSelection,
  );

  // -----------------------------------------------------------------------------
  // ************************** ------------ **************************
  // *                        ITEMS MANAGEMENT                        *
  // ************************** ------------ **************************

  const leftNavItemsProps = React.useMemo(
    () => ({
      headerItem: propsWithDefaults.HeaderComponent,
      bodyHeaderItem: propsWithDefaults.BodyHeaderComponent,
      items: propsWithDefaults.items,
      openedDrilldowns,
    }),
    [
      propsWithDefaults.HeaderComponent,
      propsWithDefaults.BodyHeaderComponent,
      propsWithDefaults.items,
      openedDrilldowns,
    ],
  );

  // We need to know the current visible items since we have drilldowns
  const { visibleItems, visibleItemsRefs } = useLeftNavItems(leftNavItemsProps);

  // -----------------------------------------------------------------------------
  // ************************** ------------ **************************
  // *                         LEFTNAV CONFIG                         *
  // ************************** ------------ **************************
  return React.useMemo(
    () => ({
      leftNavProps: propsWithDefaults,
      expandedForAnimation,
      selectedItem: propsWithDefaults.selectedItem !== undefined ? propsWithDefaults.selectedItem : selectedItem,
      setSelectedItem,
      selectedParent:
        propsWithDefaults.selectedParent !== undefined ? propsWithDefaults.selectedParent : selectedParent,
      setSelectedParent,
      focusedItem,
      setFocusedItem,
      openedDrilldowns,
      setOpenedDrilldowns,
      visibleItems,
      visibleItemsRefs,
    }),
    [
      propsWithDefaults,
      expandedForAnimation,
      selectedItem,
      selectedParent,
      focusedItem,
      openedDrilldowns,
      visibleItems,
      visibleItemsRefs,
    ],
  );
};

export default useLeftNavConfig;
